import { AlertTriangle, BarChart3, Inbox, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";
import {
  ChartSkeleton,
  GaugeSkeleton,
  KpiGridSkeleton,
  PodiumSkeleton,
  RankingSkeleton,
} from "@/components/skeletons";

export type TabSkeletonVariant =
  | "global"
  | "teams"
  | "streaks"
  | "movers"
  | "efficiency"
  | "burn"
  | "achievements"
  | "pulse";

/**
 * Tab-aware loading placeholder. Each variant composes the specific
 * skeleton primitives so the skeleton mirrors the real layout and
 * avoids layout shift when data arrives.
 *
 * Variant is optional — legacy callers using <TabSkeleton /> get the
 * generic "global" layout.
 */
export function TabSkeleton({
  variant = "global",
  className,
}: {
  variant?: TabSkeletonVariant;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-5", className)} aria-busy="true">
      {renderVariant(variant)}
    </div>
  );
}

function renderVariant(variant: TabSkeletonVariant): ReactNode {
  switch (variant) {
    case "teams":
      return (
        <>
          <KpiGridSkeleton count={3} />
          <RankingSkeleton rows={8} />
        </>
      );

    case "streaks":
    case "movers":
      return (
        <div className="grid grid-cols-2 gap-4">
          <RankingSkeleton rows={6} />
          <RankingSkeleton rows={6} />
        </div>
      );

    case "burn":
      return (
        <>
          <div className="grid grid-cols-[280px_1fr] gap-4">
            <GaugeSkeleton size={240} />
            <KpiGridSkeleton count={3} />
          </div>
          <RankingSkeleton rows={8} />
        </>
      );

    case "pulse":
      return (
        <>
          <KpiGridSkeleton count={4} />
          <ChartSkeleton height={240} />
          <ChartSkeleton height={200} />
        </>
      );

    case "global":
    case "efficiency":
    case "achievements":
    default:
      return (
        <>
          <KpiGridSkeleton count={4} />
          <PodiumSkeleton />
          <RankingSkeleton rows={8} />
        </>
      );
  }
}

/**
 * Empty state with a decorative illustration — concentric rings behind a
 * lucide glyph, sitting on a faint radial glow. Accepts a custom icon,
 * optional description and a call-to-action.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string;
  description?: string;
  /** Lucide icon component. Defaults to Inbox. */
  icon?: React.ComponentType<{ className?: string }>;
  action?: ReactNode;
  className?: string;
}) {
  const Icon = icon ?? Inbox;

  return (
    <div
      className={cn(
        "relative flex flex-col items-center justify-center overflow-hidden rounded-[20px] border border-border-1 bg-white/[0.02] px-6 py-16 text-center",
        className,
      )}
    >
      {/* Soft radial glow background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(167,139,250,0.08)_0%,transparent_60%)]"
      />

      {/* Concentric rings + icon */}
      <div
        aria-hidden="true"
        className="relative mb-6 flex h-28 w-28 items-center justify-center"
      >
        <svg
          viewBox="0 0 120 120"
          className="absolute inset-0 h-full w-full"
        >
          <defs>
            <linearGradient id="empty-ring" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(167,139,250,0.35)" />
              <stop offset="100%" stopColor="rgba(236,72,153,0.15)" />
            </linearGradient>
          </defs>
          <circle
            cx="60"
            cy="60"
            r="56"
            fill="none"
            stroke="url(#empty-ring)"
            strokeWidth="1"
            strokeDasharray="3 5"
          />
          <circle
            cx="60"
            cy="60"
            r="42"
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="1"
          />
          <circle
            cx="60"
            cy="60"
            r="28"
            fill="rgba(255,255,255,0.02)"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="1"
          />
        </svg>
        <Icon className="relative h-8 w-8 text-text-3" />
      </div>

      <div className="relative font-display text-lg font-semibold text-text-1">
        {title}
      </div>
      {description && (
        <p className="relative mt-2 max-w-md text-sm text-text-3">
          {description}
        </p>
      )}
      {action && <div className="relative mt-5">{action}</div>}
    </div>
  );
}

/** Re-export a couple of common lucide glyphs for the `icon` prop — avoids
 *  forcing every caller to add lucide-react imports just to pick an icon. */
export { BarChart3 as EmptyStateChartIcon, Inbox as EmptyStateInboxIcon };

/**
 * Error surface. When `onRetry` is provided (TanStack `refetch`), renders a
 * Reintentar button styled to match the rose card.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message =
    error instanceof Error ? error.message : "Error desconocido";

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col gap-3 rounded-[20px] border border-rose-500/30 bg-rose-500/10 p-6 text-sm text-rose-200",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-rose-500/20 ring-1 ring-rose-500/30">
          <AlertTriangle className="h-4 w-4 text-rose-300" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-rose-100">
            No pudimos cargar esta vista.
          </div>
          <div className="mt-1 break-words text-rose-200/80">{message}</div>
        </div>
      </div>

      {onRetry && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onRetry}
            className={cn(
              "inline-flex items-center gap-2 rounded-lg border border-rose-400/40 bg-rose-500/15 px-3 py-1.5 text-xs font-medium text-rose-100",
              "transition hover:bg-rose-500/25 hover:border-rose-400/60",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-300",
            )}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
}

export function SectionTitle({
  children,
  right,
}: {
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-3 flex items-end justify-between gap-4">
      <h2 className="font-display text-sm font-semibold uppercase tracking-[0.2em] text-text-3">
        {children}
      </h2>
      {right}
    </div>
  );
}
